/**
 * Referral system (Phase 3): process ref_ on start, get link and count.
 */

import { cloud } from './cloudStorage';
import {
  getMe,
  getPublicAppConfig,
  getToken,
  processReferral as apiProcessReferral,
} from '../lib/api';

const REFERRAL_PROCESSED_KEY = 'referral_processed';
const PENDING_REFERRER_KEY = 'pending_referrer_id';
const REFERRAL_TELEGRAM_ID_CACHE_KEY = 'referral_telegram_id';

const ENV = import.meta.env as Record<string, string | undefined>;
const BOT_USERNAME_FROM_ENV = normalizeBotUsername(
  ENV.VITE_BOT_USERNAME ?? ENV.VITE_PUBLIC_BOT_USERNAME,
);
let cachedBotUsername: string | null = BOT_USERNAME_FROM_ENV;
let hasResolvedBotUsername = BOT_USERNAME_FROM_ENV !== null;
let botUsernameRequest: Promise<string | null> | null = null;

function normalizeBotUsername(raw: string | null | undefined): string | null {
  const username = String(raw ?? '').trim().replace(/^@+/, '');
  return username || null;
}

function getTg() {
  if (typeof window === 'undefined') return undefined;
  return window.Telegram?.WebApp;
}

function getTelegramIdFromUnsafe(): number | null {
  const id = getTg()?.initDataUnsafe?.user?.id;
  return typeof id === 'number' && Number.isFinite(id) && id > 0 ? id : null;
}

function getTelegramIdFromInitData(): number | null {
  const initData = getTg()?.initData;
  if (!initData) return null;
  try {
    const params = new URLSearchParams(initData);
    const rawUser = params.get('user');
    if (!rawUser) return null;
    const parsed = JSON.parse(rawUser) as { id?: unknown };
    const id = typeof parsed.id === 'number' ? parsed.id : Number(parsed.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function getCachedTelegramId(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(REFERRAL_TELEGRAM_ID_CACHE_KEY);
    if (!raw) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function cacheTelegramId(id: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REFERRAL_TELEGRAM_ID_CACHE_KEY, String(id));
  } catch {
    // ignore
  }
}

async function resolveCurrentTelegramId(): Promise<number | null> {
  const fromUnsafe = getTelegramIdFromUnsafe();
  if (fromUnsafe) {
    cacheTelegramId(fromUnsafe);
    return fromUnsafe;
  }

  const fromInitData = getTelegramIdFromInitData();
  if (fromInitData) {
    cacheTelegramId(fromInitData);
    return fromInitData;
  }

  const fromCache = getCachedTelegramId();
  if (fromCache) return fromCache;

  if (!getToken()) return null;
  try {
    const me = await getMe();
    const id = typeof me.telegram_id === 'number' ? me.telegram_id : Number(me.telegram_id);
    if (Number.isFinite(id) && id > 0) {
      cacheTelegramId(id);
      return id;
    }
    return null;
  } catch {
    return null;
  }
}

function getStartParamFromLocation(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const url = new URL(window.location.href);
    const fromSearch =
      url.searchParams.get('startapp') ??
      url.searchParams.get('tgWebAppStartParam') ??
      url.searchParams.get('start_param');
    if (fromSearch?.trim()) return fromSearch.trim();
  } catch {
    // ignore
  }

  const hash = String(window.location.hash || '').replace(/^#/, '');
  if (!hash) return null;
  const queryStart = hash.indexOf('?');
  const rawQuery = queryStart >= 0 ? hash.slice(queryStart + 1) : hash;
  if (!rawQuery) return null;

  const params = new URLSearchParams(rawQuery);
  const fromHash =
    params.get('startapp') ??
    params.get('tgWebAppStartParam') ??
    params.get('start_param');
  return fromHash?.trim() || null;
}

export function getTelegramStartParam(): string | null {
  const tgStartParam = getTg()?.initDataUnsafe?.start_param;
  if (tgStartParam?.trim()) return tgStartParam.trim();
  return getStartParamFromLocation();
}

function extractReferrerId(startParam: string | null, myId: number): number | null {
  if (!startParam || !startParam.startsWith('ref_')) return null;
  const parsed = Number.parseInt(startParam.slice(4), 10);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed === myId) return null;
  return parsed;
}

function getBotUsernameFromTelegram(): string | null {
  const tg = getTg();
  const unsafe = tg?.initDataUnsafe as
    | { receiver?: { username?: string }; chat?: { username?: string } }
    | undefined;
  return (
    normalizeBotUsername(unsafe?.receiver?.username) ??
    normalizeBotUsername(unsafe?.chat?.username)
  );
}

async function resolveBotUsername(): Promise<string | null> {
  const runtimeUsername = getBotUsernameFromTelegram();
  if (runtimeUsername) {
    cachedBotUsername = runtimeUsername;
    hasResolvedBotUsername = true;
    return runtimeUsername;
  }

  if (hasResolvedBotUsername) return cachedBotUsername;

  if (!botUsernameRequest) {
    botUsernameRequest = getPublicAppConfig()
      .then((config) => {
        const normalized = normalizeBotUsername(config.bot_username);
        cachedBotUsername = normalized;
        hasResolvedBotUsername = true;
        return normalized;
      })
      .catch(() => null)
      .finally(() => {
        botUsernameRequest = null;
      });
  }

  return botUsernameRequest;
}

function buildWebAppReferralFallback(myId: number): string {
  if (typeof window === 'undefined') return '';
  try {
    const url = new URL(`${window.location.origin}${window.location.pathname}`);
    url.searchParams.set('startapp', `ref_${myId}`);
    return url.toString();
  } catch {
    return '';
  }
}

function isTerminalReferralResponse(message: string | undefined): boolean {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('already') ||
    normalized.includes('cannot refer yourself')
  );
}

/**
 * Call once on app init. If start_param is ref_{telegram_id}, records referral and marks processed.
 * If no token yet, stores pending referrer; call again after auth (e.g. when ReferralCard loads).
 */
export async function processReferralOnStart(): Promise<void> {
  const myId = await resolveCurrentTelegramId();
  if (!myId) return;

  const startParam = getTelegramStartParam();
  let referrerId = extractReferrerId(startParam, myId);

  if (referrerId != null) {
    await cloud.set(PENDING_REFERRER_KEY, referrerId);
  } else {
    const pending = await cloud.get<number>(PENDING_REFERRER_KEY);
    if (pending != null && pending !== myId) referrerId = pending;
    else return;
  }

  const alreadyProcessed = await cloud.get<boolean>(REFERRAL_PROCESSED_KEY);
  if (alreadyProcessed) return;

  try {
    const res = await apiProcessReferral(referrerId);
    if (res?.recorded !== false || isTerminalReferralResponse(res?.message)) {
      await cloud.set(REFERRAL_PROCESSED_KEY, true);
      await cloud.remove(PENDING_REFERRER_KEY);
    }
  } catch (err) {
    console.error('Referral processing error:', err);
  }
}

/**
 * Referral link for the current user (to share).
 */
export async function getReferralLink(): Promise<string> {
  const myId = await resolveCurrentTelegramId();
  if (!myId) return '';

  const botUsername = await resolveBotUsername();
  if (botUsername) return `https://t.me/${botUsername}?startapp=ref_${myId}`;
  return buildWebAppReferralFallback(myId);
}
